import React, { useEffect, useState } from 'react'
import { DataTable, DonutChart, LineChart } from '@egw/components'
import { configClient } from '@egw/config'
import { useAuth } from '@egw/auth'
import { type ValueFormat } from '@egw/utils'

interface DataVisualizationProps {
  threadId: string
  fileId: string
  showBorder?: boolean
}

interface Config {
  aiChat?: {
    runtimeUrl?: string
    apiUrl?: string
  }
}

interface DataFileResponse {
  data?: unknown[]
  title?: string
  displayType?: string
  xAxisLabel?: string
  yAxisLabel?: string
  valueFormat?: ValueFormat
  columnFormats?: Record<string, ValueFormat>
}

export const DataVisualization: React.FC<DataVisualizationProps> = ({ threadId, fileId, showBorder = true }) => {
  const config = configClient.getConfig() as Config
  const { accessToken } = useAuth()

  const [data, setData] = useState<any[]>([])
  const [title, setTitle] = useState<string>('')
  const [displayType, setDisplayType] = useState<string>('')
  const [xAxisLabel, setXAxisLabel] = useState<string | undefined>(undefined)
  const [yAxisLabel, setYAxisLabel] = useState<string | undefined>(undefined)
  const [valueFormat, setValueFormat] = useState<ValueFormat>(undefined)
  const [columnFormats, setColumnFormats] = useState<Record<string, ValueFormat> | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        if (!threadId) {
          throw new Error('Thread ID is required')
        }

        if (!accessToken) {
          throw new Error('Access token is required')
        }

        if (!config.aiChat?.apiUrl) {
          throw new Error('API URL is required')
        }

        const token: string = accessToken
        const apiUrl: string = config.aiChat.apiUrl
        const response = await fetch(`${apiUrl}/threads/${threadId}/files/${fileId}`, {
          headers: {
            authorization: `Bearer ${token}`,
          },
        })
        if (!response.ok) {
          throw new Error(`Failed to fetch data: ${response.statusText}`)
        }
        const jsonResponse = (await response.json()) as DataFileResponse

        // Handle format with title, data, displayType, and formatting properties
        setData(jsonResponse.data || [])
        setTitle(jsonResponse.title || '')
        setDisplayType(jsonResponse.displayType || '')
        setXAxisLabel(jsonResponse.xAxisLabel)
        setYAxisLabel(jsonResponse.yAxisLabel)
        setValueFormat(jsonResponse.valueFormat)
        setColumnFormats(jsonResponse.columnFormats)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data')
      } finally {
        setLoading(false)
      }
    }

    if (fileId && threadId) {
      void fetchData()
    }
  }, [fileId, threadId])

  if (loading) {
    return (
      <div className="p-4 border rounded animate-pulse">
        <div className="text-center">Loading data...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 border rounded bg-red-50">
        <div className="text-red-600">Error: {error}</div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="p-4 border rounded bg-yellow-50">
        <div className="text-yellow-600">No data available</div>
      </div>
    )
  }

  if (!displayType) {
    return (
      <div className="p-4 border rounded bg-yellow-50">
        <div className="text-yellow-600">No display type specified</div>
      </div>
    )
  }

  const renderChart = () => {
    switch (displayType) {
      case 'data-table':
        return <DataTable data={data} title={title} valueFormat={valueFormat} columnFormats={columnFormats} />

      case 'line-chart':
        return <LineChart data={data} xAxisLabel={xAxisLabel} yAxisLabel={yAxisLabel} valueFormat={valueFormat} />

      case 'pie-chart':
        return <DonutChart data={data} valueFormat={valueFormat} />

      default:
        return (
          <div className="p-4 border rounded bg-yellow-50">
            <div className="text-yellow-600">Unsupported display type: {displayType}</div>
          </div>
        )
    }
  }

  const containerClasses = showBorder ? 'p-4 border rounded bg-white my-6' : 'p-4 bg-white'

  return (
    <div className={containerClasses}>
      <div className="text-sm font-medium mb-4 text-center" style={{ color: '#64748b' }}>
        {title || `${displayType.replace('-', ' ')} Visualization`}
      </div>
      <div className="min-h-[400px]">{renderChart()}</div>
    </div>
  )
}
